import { getConfig } from "@/config";
import { isSessionInvalid, useAuthStore } from "@/store/authStore";

export class ApiError extends Error {
  public detail: unknown;
  constructor(
    public status: number,
    message: string,
    public errorCode?: string,
    detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.detail = detail ?? message;
  }
}

// Single in-flight refresh shared across concurrent 401s. Without
// this, parallel calls that all hit 401 at once skipped the refresh
// and threw silent errors — panels stayed blank with no logout. The
// in-flight Promise lets every caller await the same refresh.
let inFlightRefresh: Promise<void> | null = null;

/** Reasons a person can be sent back to the sign-in page.
 *
 *  Carried in the URL so the login screen can SAY why (MPPI-00876). Landing
 *  on a bare login form after working in the admin reads as a bug — or
 *  worse, as the data having gone — which is exactly how 2026-10-20's
 *  "I see zero in the data" began. */
export type LogoutReason = "expired" | "revoked" | "inactive" | "signed-out";

function redirectToLogin(reason: LogoutReason = "expired"): void {
  if (typeof window === "undefined") return;
  // Avoid bouncing if we're already on /login (e.g. after a logout
  // navigate from RequireAuth) — the SPA router would re-mount and
  // trigger another redirect.
  if (window.location.pathname === "/login") return;
  const returnUrl = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  window.location.href = `/login?returnUrl=${returnUrl}&reason=${reason}`;
}

export { redirectToLogin };

export async function adminFetch<T>(
  path: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<T> {
  const config = getConfig();
  const token = useAuthStore.getState().accessToken;

  // For FormData uploads we must NOT set Content-Type — the browser
  // adds it with the multipart boundary. Inserting a fixed JSON
  // content type would corrupt the upload.
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const baseHeaders: Record<string, string> = isFormData
    ? {}
    : { "Content-Type": "application/json" };

  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...baseHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  // A 401 whose body says MFA_REQUIRED is NOT an expired token — the token is
  // perfectly valid, the 2FA marker has just aged out (8h). Refreshing it
  // succeeds and the retry 401s again, so before this the user sat looking at
  // broken panels rather than being asked for a code (MPPI-00286). Handled
  // ahead of the refresh path because the refresh would "work" and prove
  // nothing.
  if (res.status === 401) {
    const peek = await res
      .clone()
      .json()
      .catch(() => null);
    const code = peek?.detail?.code ?? peek?.error?.code;
    if (code === "MFA_REQUIRED") {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/mfa")) {
        window.location.assign("/mfa");
      }
      throw new ApiError(401, "Two-factor verification required");
    }
  }

  if (res.status === 401 && !isRetry) {
    // Coalesce concurrent 401s onto a single refresh — every caller
    // awaits the same Promise instead of skipping it and failing
    // silently.
    if (!inFlightRefresh) {
      inFlightRefresh = useAuthStore
        .getState()
        .refreshToken()
        .finally(() => {
          inFlightRefresh = null;
        });
    }
    try {
      await inFlightRefresh;
      return adminFetch(path, options, true);
    } catch (err) {
      // Only sign out when the SESSION is invalid (MPPI-00842).
      // `logout()` calls Cognito's `signOut()`, which clears localStorage
      // for the whole origin — so a dropped connection during a refresh
      // used to sign the person out of every tab. The tab in front of
      // them kept working from its in-memory token and still looked
      // signed in; the next tab they opened found nothing.
      if (isSessionInvalid(err)) {
        useAuthStore.getState().logout();
        redirectToLogin("revoked");
        throw new ApiError(401, "Session expired");
      }
      throw new ApiError(
        503,
        "Could not reach the sign-in service. Your session is intact — retry in a moment.",
      );
    }
  }

  // A RETRIED request that still 401s is a dead session, and nothing above
  // catches it (MPPI-00875).
  //
  // The branch above only runs when `!isRetry`. So the sequence "401 ->
  // refresh succeeds -> retry -> 401 again" fell straight through to the
  // generic error below: no logout, no redirect, just a rejected promise
  // per panel. Every panel renders its empty state and the operator sees a
  // fully drawn admin with ZERO DATA and no prompt to sign in — reported
  // 2026-10-20, with `agents.last_login_at` a day old and exactly two 401s
  // on the wire.
  //
  // MPPI-00842 fixed the opposite fault — logging out on a transient
  // network failure, which cleared localStorage for every tab. Narrowing
  // that left this case uncovered. A refresh that "worked" and still buys
  // a 401 is the one state where signing out is unambiguously right: the
  // token is live, the API refuses it, and retrying cannot help.
  if (res.status === 401 && isRetry) {
    useAuthStore.getState().logout();
    redirectToLogin("expired");
    throw new ApiError(401, "Session expired — please sign in again");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: { message: res.statusText } }));
    // The API wraps errors as `{error: {code, message, request_id, details?}}`
    // (see main.py _http_exception_handler + middleware/error_handler.py).
    // FastAPI 422 validation errors come back as `{detail: [...]}` — fall
    // back to that shape too.
    const errEnvelope = (body as { error?: { code?: string; message?: string; details?: unknown } }).error;
    const legacyDetail = (body as { detail?: unknown }).detail;

    let message = "Unknown error";
    let code: string | undefined;
    let detail: unknown = body;

    if (errEnvelope && typeof errEnvelope.message === "string") {
      message = errEnvelope.message;
      code = errEnvelope.code;
      detail = errEnvelope;
    } else if (typeof legacyDetail === "string") {
      message = legacyDetail;
    } else if (Array.isArray(legacyDetail) && legacyDetail.length > 0) {
      // FastAPI 422 — array of {loc, msg, type}
      const first = legacyDetail[0] as { msg?: string; loc?: unknown[] };
      message = first.msg ?? "Validation error";
      detail = legacyDetail;
    } else {
      message = res.statusText || "Unknown error";
    }

    throw new ApiError(res.status, message, code, detail);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}
